import { useEffect, useState } from "react"

/**
 * A `now` that changes when the next session deadline passes.
 *
 * Expiry is derived, never stored — no client is awake at the moment a session
 * runs out to write a status for it (see `utils/sessionLifecycle`). The default
 * `Date.now()` inside `sessionState` therefore only advances when React happens
 * to render anyway, which on a one-shot index screen or an idle open session is
 * never: a row keeps saying Live, and its controls keep looking usable, for as
 * long as the tab stays open.
 *
 * So the deadline schedules its own render. One timeout at the SOONEST future
 * deadline rather than a heartbeat: a list of twenty sessions has at most one
 * next thing to happen, and re-rendering every second to discover that nothing
 * has is worse than the bug.
 *
 * Pass the `expiresAt` of everything on screen; nulls (sessions with no
 * deadline) and deadlines already behind us contribute nothing.
 */
export function useExpiryTick(expiries: readonly (number | null)[]): number {
  const [now, setNow] = useState(() => Date.now())

  const next = expiries.reduce<number | null>((soonest, at) => {
    if (at === null || at <= now) return soonest
    return soonest === null || at < soonest ? at : soonest
  }, null)

  useEffect(() => {
    if (next === null) return
    // A second past the deadline, so the render that fires is unambiguously
    // after it rather than racing the millisecond it lands on.
    const id = window.setTimeout(() => setNow(Date.now()), Math.max(next - Date.now(), 0) + 1000)
    return () => window.clearTimeout(id)
  }, [next])

  return now
}
